import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { RecordData } from 'src/app/types';
import { RecordingEvent, SeekEvent } from '../../measured-data-details.types';
import { MeasurementEvent, ParsedMeasurement, RsstKpi } from '../../models/parsed-measurement.model';
import { EChartsOption } from 'echarts';
import { NeckOrientation } from '../../services/neck-angle.service';

import { DetailsHeaderComponent } from '../shared/details-header/details-header.component';
import { EventTableComponent } from '../shared/event-table/event-table.component';
import { DataFooterComponent } from '../shared/data-footer/data-footer.component';
import { DisclaimerComponent } from '../shared/disclaimer/disclaimer.component';
import { ReferenceCardComponent, SearchLink } from '../shared/reference-card/reference-card.component';
import { RsstDonutComponent } from '../shared/rsst-donut/rsst-donut.component';
import { MeasuredDataWaveCardComponent } from '../wave-card/measured-data-wave-card.component';
import { MeasuredDataNeckAngleCardComponent } from '../neck-angle-card/measured-data-neck-angle-card.component';
import { MatIconModule } from '@angular/material/icon';
import type { ECharts } from 'echarts';

@Component({
  selector: 'app-protocol-rsst',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatIconModule,
    DetailsHeaderComponent,
    EventTableComponent,
    DataFooterComponent,
    DisclaimerComponent,
    ReferenceCardComponent,
    RsstDonutComponent,
    MeasuredDataWaveCardComponent,
    MeasuredDataNeckAngleCardComponent,
  ],
  templateUrl: './protocol-rsst.component.html',
  styleUrl: './protocol-rsst.component.scss',
})
export class ProtocolRsstComponent implements OnDestroy {
  protected readonly headerTitle = $localize`:@@details.protocolRsst.headerTitle:RSST｜反復唾液嚥下テスト`;
  protected readonly headerBadgeText = $localize`:@@details.protocolRsst.headerBadgeText:スクリーニング`;

  // ── Data inputs ──
  readonly recordData = input.required<RecordData>();
  readonly parsedMeasurement = input<ParsedMeasurement | null>(null);
  readonly pieChartData = input<number[]>([]);
  readonly pieChartColor = input<string[]>([]);

  // ── Wave card inputs ──
  readonly recordtype = input<number>(0);
  readonly recordLoadingSuccess = input<boolean>(true);
  readonly waveLoadingPercent = input<number | null>(null);
  readonly waveProcessing = input<boolean>(false);
  readonly waveDownloading = input<boolean>(false);
  readonly waveDownloadPercent = input<number | null>(null);
  readonly waveErrorMessage = input<string | null>(null);
  readonly selectedEvent = input<RecordingEvent | null>(null);
  readonly currentStartOffset = input<number>(0);
  readonly playing = input<boolean>(false);
  readonly timelineOffsetReference = input<number>(0);
  readonly zoomValue = input<number>(0);
  readonly showWaveCard = input<boolean>(true);

  // ── Neck angle inputs ──
  readonly chartOption = input<EChartsOption>({});
  readonly currentChartOption = input<EChartsOption>({});
  readonly currentOrientation = input<NeckOrientation | null>(null);

  // ── Wave card outputs ──
  @Output() readonly offsetToggle = new EventEmitter<boolean>();
  @Output() readonly seek = new EventEmitter<number>();
  @Output() readonly eventSeek = new EventEmitter<SeekEvent>();
  @Output() readonly playPauseClicked = new EventEmitter<void>();
  @Output() readonly zoomMinusClicked = new EventEmitter<void>();
  @Output() readonly zoomResetClicked = new EventEmitter<void>();
  @Output() readonly zoomPlusClicked = new EventEmitter<void>();

  // ── Neck angle outputs ──
  @Output() readonly chartInit = new EventEmitter<ECharts>();
  @Output() readonly currentChartInit = new EventEmitter<ECharts>();

  /** 親コンポーネントが wave-card の waveContainer を取得するためのイベント */
  @Output() readonly waveHostReady = new EventEmitter<MeasuredDataWaveCardComponent>();
  private destroyed = false;
  private latestWaveCardRef?: MeasuredDataWaveCardComponent;

  @ViewChild(MeasuredDataWaveCardComponent)
  set waveCardRef(comp: MeasuredDataWaveCardComponent | undefined) {
    if (!comp || this.destroyed) return;
    this.latestWaveCardRef = comp;
    queueMicrotask(() => {
      if (this.destroyed || this.latestWaveCardRef !== comp) {
        return;
      }
      this.waveHostReady.emit(comp);
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.latestWaveCardRef = undefined;
  }

  // ── Computed ──
  protected readonly patientName = computed(() => {
    const d = this.recordData()?.data;
    return d?.userName ?? '';
  });

  protected readonly measureDate = computed(() => {
    const d = this.recordData()?.data;
    const createdAt = d?.createdAt ?? d?.recordedAt ?? '';
    const offsetMatch = createdAt.match(/([+-])(\d{2}):(\d{2})$/);
    const hasNonZeroOffset =
      offsetMatch &&
      !(offsetMatch[1] === '+' && offsetMatch[2] === '00' && offsetMatch[3] === '00');

    if (hasNonZeroOffset && d?.createdAtLocationStr) {
      const dateStr = d.createdAtLocationStr.replace(/[+-]\d{2}:\d{2}$/, '').trim();
      if (!dateStr) return '';
      const h = parseInt(offsetMatch[2], 10);
      const m = parseInt(offsetMatch[3], 10);
      const tzLabel = m > 0
        ? `UTC${offsetMatch[1]}${h}:${String(m).padStart(2, '0')}`
        : `UTC${offsetMatch[1]}${h}`;
      return `${dateStr} (${tzLabel})`;
    }

    const raw = d?.createdAtLocalStr ?? d?.recordedAt ?? createdAt;
    const dateStr = raw.replace(/[+-]\d{2}:\d{2}$/, '').trim();
    if (!dateStr) return '';
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return tz ? `${dateStr} (${tz})` : dateStr;
    } catch {
      return dateStr;
    }
  });


  protected readonly measurer = computed(() => {
    const rd = this.recordData();
    const d = rd?.data;
    if (d?.recorderName) {
      return $localize`:@@details.protocolRsst.measurer:計測者: ${d.recorderName}:recorderName:`;
    }
    const m = rd?.meta?.measurer;
    if (m?.lastName || m?.firstName) {
      const name = (m.lastName ?? '') + (m.firstName ?? '');
      return $localize`:@@details.protocolRsst.measurer:計測者: ${name}:recorderName:`;
    }
    return '';
  });

  protected readonly rsstKpi = computed<RsstKpi | undefined>(() =>
    this.parsedMeasurement()?.rsstKpi,
  );

  protected readonly isBelowThreshold = computed(() =>
    this.rsstKpi()?.isBelowThreshold ?? false,
  );

  protected readonly events = computed<MeasurementEvent[]>(() =>
    this.parsedMeasurement()?.events ?? [],
  );

  protected readonly dataId = computed(() =>
    this.recordData()?.id ?? '',
  );

  protected readonly downloadUrl = computed(() => {
    const d = this.recordData()?.data;
    return d?.storage?.csvUrl ?? '';
  });

  protected readonly swallowCount = computed(() =>
    this.rsstKpi()?.swallowCount ?? 0,
  );

  protected readonly meanInterval = computed(() => {
    const interval = this.rsstKpi()?.meanSwallowInterval ?? 0;
    return interval > 0 ? interval.toFixed(1) : '—';
  });

  protected readonly evaluation = computed(() =>
    this.rsstKpi()?.evaluation ?? 0,
  );

  protected readonly donutLegend = computed(() => {
    const colors = this.pieChartColor();
    return [
      { label: $localize`:@@details.protocolRsst.donutLegend.swallowPresent:嚥下あり`, color: colors[1] ?? '#4fc3f7' },
    ];
  });

  protected readonly searchLinks = computed<SearchLink[]>(() => {
    const links: SearchLink[] = [
      { label: $localize`:@@details.protocolRsst.searchLink.rsst:RSST（反復唾液嚥下テスト）`, url: 'https://www.google.com/search?q=RSST+反復唾液嚥下テスト' },
    ];
    if (this.isBelowThreshold()) {
      links.push(
        { label: $localize`:@@details.protocolRsst.searchLink.belowThreshold:RSST 基準値以下`, url: 'https://www.google.com/search?q=RSST+基準値以下' },
        { label: $localize`:@@details.protocolRsst.searchLink.rehabilitation:RSST 基準値以下 リハビリテーション`, url: 'https://www.google.com/search?q=RSST+基準値以下+リハビリテーション' },
      );
    }
    return links;
  });

  protected readonly disclaimer = $localize`:@@details.protocolRsst.disclaimer:RSSTはスクリーニング検査です。嚥下音の自動識別にはAI推定値を含みます。結果は専門医の判断と併せてご利用ください。`;

  protected readonly playbackButtonDisabled = computed(() =>
    !this.recordLoadingSuccess()
    || this.waveProcessing()
    || this.waveDownloading()
    || this.waveLoadingPercent() !== null,
  );

  protected onEventSeek(event: SeekEvent): void {
    this.eventSeek.emit(event);
  }
}
